import React, { useCallback, useEffect, useRef, useState } from "react";
import get from "lodash/get";
import { array, bool, func, node, number, object } from "prop-types";

import { getRelatedStories, getStoryById } from "../../../api/utils";
import { getStoryUrl } from "../../arrow/utils/utils";
import { RELATED_STORY_FIELDS } from "../../constants";
import { addDatalayerEventStory, fireComscorePageView } from "../utils";
import StoryWrapper from "../story-templates/story-wrapper";
import "./story.m.css";

const DEFAULT_TEMPLATES = ["text", "magazine"];
const BLOCKED_FOR_INDIA_ATTRIBUTE = ["metadata", "story-attributes", "is-blocked-for-india"];
const PERPETUAL_STORY_PREFETCH_ROOT_MARGIN = "2000px 0px";
const PERPETUAL_STORY_SCROLL_PREFETCH_OFFSET = 500;

const getStoryContentId = (story = {}) => story["story-content-id"] || story.id;

const normalizeStoryResponse = (response) => get(response, ["story"], response);

const getStoryTitle = (story = {}) => get(story, ["seo", "meta-title"], "") || get(story, ["headline"], "");

const getPrimarySectionId = (story = {}) => get(story, ["sections", 0, "id"], "");
const hasPrimarySection = (story = {}, sectionId) => getPrimarySectionId(story) === sectionId;

const transformEntityLinkText = (text = "", sketchesHost = "") => {
  if (typeof text !== "string" || !text.includes('class="entity-link"')) return text;

  return text
    .replace(/href="\/company\//g, `href="${sketchesHost}/companies/`)
    .replace(/href="\/individual\//g, `href="${sketchesHost}/people/`)
    .replace(/href="https:\/\/[^"]*\/company\//g, `href="${sketchesHost}/companies/`)
    .replace(/href="https:\/\/[^"]*\/individual\//g, `href="${sketchesHost}/people/`);
};

const normalizePerpetualStoryEntityLinks = (story = {}, sketchesHost = "") => {
  if (!story || !Array.isArray(story.cards)) return story;

  return {
    ...story,
    cards: story.cards.map((card) => ({
      ...card,
      "story-elements": get(card, ["story-elements"], []).map((element) =>
        typeof element?.text === "string"
          ? { ...element, text: transformEntityLinkText(element.text, sketchesHost) }
          : element
      ),
    })),
  };
};

const isStoryBlockedForIndia = (story = {}) => {
  const blockedValues = get(story, BLOCKED_FOR_INDIA_ATTRIBUTE, []);

  if (Array.isArray(blockedValues)) {
    return blockedValues.some((value) => value === "true");
  }

  return blockedValues === "true";
};

const getUniqueEligibleStories = (stories = [], { sectionId, templates, excludedStoryIds = new Set() }) => {
  const seenStoryIds = new Set(excludedStoryIds);

  return stories
    .filter((relatedStory) => templates.includes(relatedStory["story-template"]))
    .filter((relatedStory) => hasPrimarySection(relatedStory, sectionId))
    .filter((relatedStory) => !isStoryBlockedForIndia(relatedStory))
    .filter((relatedStory) => {
      const relatedStoryId = getStoryContentId(relatedStory);
      if (!relatedStoryId || seenStoryIds.has(relatedStoryId)) return false;
      seenStoryIds.add(relatedStoryId);
      return true;
    });
};

let activeStoryBrowserStateId = null;
let activePerpetualStoryItemId = null;
let perpetualStoryItems = [];
let perpetualStoryTicking = false;
let perpetualStoryListenerCount = 0;

const sortStoryItemsByDomOrder = (items = []) =>
  [...items].sort((firstItem, secondItem) => {
    if (!firstItem.node || !secondItem.node || firstItem.node === secondItem.node) return 0;

    return firstItem.node.compareDocumentPosition(secondItem.node) & Node.DOCUMENT_POSITION_PRECEDING ? 1 : -1;
  });

const getStoryActivationTarget = (node) => node?.querySelector(".article-header-headline") || node;

const evaluateActivePerpetualStory = () => {
  perpetualStoryTicking = false;

  if (typeof window === "undefined" || !perpetualStoryItems.length) return;

  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const activeItem = sortStoryItemsByDomOrder(perpetualStoryItems).reduce((currentActiveItem, item) => {
    const activationTarget = getStoryActivationTarget(item.node);
    if (!activationTarget) return currentActiveItem;

    const rect = activationTarget.getBoundingClientRect();
    return rect.top < viewportHeight ? item : currentActiveItem;
  }, null);

  const activeStoryId = getStoryContentId(activeItem?.story);
  if (!activeItem || !activeStoryId || activeStoryId === activePerpetualStoryItemId) return;

  activePerpetualStoryItemId = activeStoryId;
  activeItem.onStoryActive(activeItem.story);
};

const requestActivePerpetualStoryEvaluation = () => {
  if (typeof window === "undefined" || perpetualStoryTicking) return;

  perpetualStoryTicking = true;
  window.requestAnimationFrame(evaluateActivePerpetualStory);
};

const registerPerpetualStoryItem = (item) => {
  if (typeof window !== "undefined" && perpetualStoryListenerCount === 0) {
    window.addEventListener("scroll", requestActivePerpetualStoryEvaluation, { passive: true });
    window.addEventListener("resize", requestActivePerpetualStoryEvaluation);
  }

  perpetualStoryListenerCount += 1;
  perpetualStoryItems = [...perpetualStoryItems, item];
  requestActivePerpetualStoryEvaluation();

  return () => {
    perpetualStoryItems = perpetualStoryItems.filter((registeredItem) => registeredItem !== item);
    perpetualStoryListenerCount = Math.max(perpetualStoryListenerCount - 1, 0);

    if (typeof window !== "undefined" && perpetualStoryListenerCount === 0) {
      window.removeEventListener("scroll", requestActivePerpetualStoryEvaluation);
      window.removeEventListener("resize", requestActivePerpetualStoryEvaluation);
    }

    if (getStoryContentId(item.story) === activePerpetualStoryItemId) {
      activePerpetualStoryItemId = null;
    }
    requestActivePerpetualStoryEvaluation();
  };
};

const syncStoryBrowserState = (story = {}, { isPerpetualStory = false } = {}) => {
  if (typeof window === "undefined") return;

  const storyId = getStoryContentId(story);
  const storyUrl = getStoryUrl(story, `/${story.slug}`);
  if (storyUrl && storyUrl !== window.location.pathname) {
    window.history.replaceState({ storyId }, "", storyUrl);
  }

  const storyTitle = typeof document !== "undefined" && getStoryTitle(story);
  if (storyTitle && storyTitle !== document.title) {
    document.title = storyTitle;
  }

  if (storyId && storyId !== activeStoryBrowserStateId) {
    activeStoryBrowserStateId = storyId;
    window.dispatchEvent(new CustomEvent("perpetual-story-active", { detail: { story, isPerpetualStory } }));
  }
};

const getPerpetualConfig = (config = {}) => {
  const perpetualScroll = get(config, ["publisher-attributes", "perpetual_scroll"], {});
  const storyCount = parseInt(get(perpetualScroll, ["story_count"], 3), 10);
  const previewParagraphCount = parseInt(get(perpetualScroll, ["continue_reading", "preview_paragraph_count"], 1), 10);
  const templates = get(perpetualScroll, ["templates"], DEFAULT_TEMPLATES);

  return {
    enabled: get(perpetualScroll, ["enabled"], false) === true,
    storyCount: Number.isFinite(storyCount) && storyCount > 0 ? storyCount : 3,
    templates: Array.isArray(templates) && templates.length > 0 ? templates : DEFAULT_TEMPLATES,
    continueReading: {
      enabled: get(perpetualScroll, ["continue_reading", "enabled"], false) === true,
      previewParagraphCount:
        Number.isFinite(previewParagraphCount) && previewParagraphCount > 0 ? previewParagraphCount : 1,
    },
  };
};

const PerpetualStoryItem = ({ children, story, onStoryActive }) => {
  const itemRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const unregisterStoryItem = registerPerpetualStoryItem({
      node: itemRef.current,
      story,
      onStoryActive,
    });

    return () => {
      unregisterStoryItem();
    };
  }, [onStoryActive, story]);

  return <div ref={itemRef}>{children}</div>;
};

PerpetualStoryItem.propTypes = {
  children: node,
  story: object,
  onStoryActive: func,
};

const PerpetualStoryFeed = ({
  story,
  config,
  isATGlobal,
  initAccessType,
  checkAccess,
  enabled,
  storyCount,
  templates,
  continueReading,
}) => {
  const [storyItems, setStoryItems] = useState([]);
  const [hasRequestedStories, setHasRequestedStories] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const sentinelRef = useRef(null);
  const trackedStoryIdsRef = useRef(new Set());
  const currentStoryId = getStoryContentId(story);
  const sectionId = getPrimarySectionId(story);

  const handleStoryActive = useCallback((activeStory) => {
    const activeStoryId = getStoryContentId(activeStory);
    syncStoryBrowserState(activeStory, { isPerpetualStory: true });

    if (activeStoryId && !trackedStoryIdsRef.current.has(activeStoryId)) {
      trackedStoryIdsRef.current.add(activeStoryId);
      addDatalayerEventStory(activeStory, "", "", true);
      fireComscorePageView(activeStory);
    }
  }, []);

  useEffect(() => {
    if (!enabled || !sectionId || !currentStoryId || hasRequestedStories) return;

    const loadStories = async () => {
      setHasRequestedStories(true);
      setIsLoading(true);

      try {
        const discoveryLimit = storyCount * 3;
        const fields = `${RELATED_STORY_FIELDS},story-content-id,sections,metadata`;
        const response = await getRelatedStories(currentStoryId, sectionId, fields, discoveryLimit);
        const relatedStories = get(response, ["stories"], []);
        const eligibleRelatedStories = getUniqueEligibleStories(relatedStories, {
          sectionId,
          templates,
          excludedStoryIds: new Set([currentStoryId]),
        });
        const selectedStories = eligibleRelatedStories.slice(0, storyCount);

        const fullStories = await Promise.all(
          selectedStories.map(async (relatedStory) => {
            const relatedStoryId = getStoryContentId(relatedStory);
            const fullStoryResponse = await getStoryById(relatedStoryId);
            return normalizePerpetualStoryEntityLinks(
              normalizeStoryResponse(fullStoryResponse),
              get(config, ["sketches-host"], "")
            );
          })
        );

        const eligibleStories = fullStories.filter(
          (fullStory) =>
            fullStory &&
            templates.includes(fullStory["story-template"]) &&
            hasPrimarySection(fullStory, sectionId) &&
            !isStoryBlockedForIndia(fullStory)
        );

        const recommendedStoryItems = eligibleStories.map((fullStory) => ({
          story: fullStory,
          recommendedStories: [],
        }));

        setStoryItems(recommendedStoryItems);
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("perpetual-stories-loaded"));
        }
      } catch (error) {
        console.error("Error loading perpetual stories:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      loadStories();
      return;
    }

    let hasStartedLoading = false;
    let sentinelObserver = null;
    const startLoading = () => {
      if (hasStartedLoading) return;

      hasStartedLoading = true;
      if (sentinelObserver) sentinelObserver.disconnect();
      window.removeEventListener("scroll", handleScrollPrefetch);
      loadStories();
    };

    const handleScrollPrefetch = () => {
      if ((window.scrollY || window.pageYOffset || 0) >= PERPETUAL_STORY_SCROLL_PREFETCH_OFFSET) {
        startLoading();
      }
    };

    sentinelObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          startLoading();
        }
      },
      { rootMargin: PERPETUAL_STORY_PREFETCH_ROOT_MARGIN }
    );

    const sentinel = sentinelRef.current;
    if (sentinel) sentinelObserver.observe(sentinel);
    window.addEventListener("scroll", handleScrollPrefetch, { passive: true });
    handleScrollPrefetch();

    return () => {
      if (sentinelObserver) sentinelObserver.disconnect();
      window.removeEventListener("scroll", handleScrollPrefetch);
    };
  }, [currentStoryId, enabled, hasRequestedStories, sectionId, storyCount, templates]);

  if (!enabled) return null;

  return (
    <>
      <div ref={sentinelRef} />
      {storyItems.map(({ story: perpetualStory, recommendedStories }, index) => (
        <React.Fragment key={getStoryContentId(perpetualStory)}>
          <div styleName="next-story-divider" aria-label="Next story">
            <div className="container">
              <span>
                NEXT STORY
                <i aria-hidden="true" />
              </span>
              {/* <i aria-hidden="true" /> */}
            </div>
          </div>
          <PerpetualStoryItem story={perpetualStory} onStoryActive={handleStoryActive}>
            <StoryWrapper
              isATGlobal={isATGlobal}
              story={perpetualStory}
              config={config}
              initAccessType={initAccessType}
              checkAccess={checkAccess}
              index={index + 1}
              recommendedStories={recommendedStories}
              disablePageAnalytics={true}
              continueReading={continueReading}
            />
          </PerpetualStoryItem>
        </React.Fragment>
      ))}
      {isLoading && <div className="perpetual-story-loading" />}
    </>
  );
};

PerpetualStoryFeed.propTypes = {
  story: object,
  config: object,
  isATGlobal: bool,
  initAccessType: func,
  checkAccess: func,
  enabled: bool,
  storyCount: number,
  templates: array,
  continueReading: object,
};

export { getPerpetualConfig, PerpetualStoryFeed, PerpetualStoryItem, syncStoryBrowserState };
